import type { Chord } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { useSearchParams } from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import { ChordProgressionPicker } from '../chord-progression-picker/chord-progression-picker.js'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { ScaleSelect } from '../scale/scale-select.js'
import { CadenceLab } from './cadence-lab.js'
import { parseCadenceLabChordsParam, parseCadenceLabScaleParam } from './cadence-lab-params.js'
import styles from './cadence-lab-page.module.css'

const MIN_CHORDS = 3
const MAX_CHORDS = 8

const noop = (): void => undefined

const buildRelatedTools = (scale: Scale): RelatedTool[] => {
  const tools: RelatedTool[] = [
    {
      targetRoute: `/app/modes?tonic=${encodeURIComponent(scale.root)}`,
      lexiconId: 'crosslink-cadence-to-mode',
    },
  ]
  // Function Quiz currently ships major + minor only; preserve key type only
  // for those two — for other modes, link to the quiz with just the root.
  if (scale.type === 'major' || scale.type === 'minor') {
    tools.push({
      targetRoute: `/app/quiz?key=${encodeURIComponent(scale.root)}&keyType=${scale.type}`,
      lexiconId: 'crosslink-cadence-to-quiz',
    })
  }
  return tools
}

/**
 * Cadence Lab standalone-route page-host wrapper.
 *
 * Reads `?scale=<root>-<type>` and `?chords=<comma-list>` from the URL,
 * derives defaults via `getDefaultProgressionForScale` (per Bowie's locked
 * per-scale-type defaults), and renders the existing `<CadenceLab>` drawer
 * with `open={true}` plus pickers for the user to change scale + progression.
 *
 * Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md
 */
export const CadenceLabPage: FC = () => {
  const [searchParams] = useSearchParams()
  const [scale, setScale] = useState<Scale>(() =>
    parseCadenceLabScaleParam(searchParams.get('scale'))
  )
  const [chords, setChords] = useState<Chord[]>(() =>
    parseCadenceLabChordsParam(searchParams.get('chords'), scale)
  )

  const handleScaleChange = (next: Scale | null): void => {
    if (!next) {
      return
    }
    setScale(next)
    // When the scale type changes, reset chords to the new scale's default
    // (a major→minor switch shouldn't leave stale major chords in the picker).
    if (next.type !== scale.type) {
      setChords(parseCadenceLabChordsParam(null, next))
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Cadence Lab</h1>
        <div className={styles.pickers}>
          <ScaleSelect value={scale} onChange={handleScaleChange} variant="all" allowNone={false} />
          <ChordProgressionPicker
            keyScale={scale}
            chords={chords}
            minChords={MIN_CHORDS}
            maxChords={MAX_CHORDS}
            onChange={setChords}
          />
        </div>
      </header>
      <CadenceLab scale={scale} chords={chords} open={true} onClose={noop} />
      <RelatedTools tools={buildRelatedTools(scale)} />
    </div>
  )
}
